import {
  executeTransition,
  TransitionPresets,
  useElementSize,
} from "@vueuse/core";
import type { CSSProperties, Ref } from "vue";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import type { Nullable } from "shared/types";

export type Model = boolean;

export interface Props {
  active?: boolean;
  boldLabel?: boolean;
  bordered?: boolean;
  caption?: string;
  componentClass?: string;
  customTrigger?: boolean;
  defaultOpened?: boolean;
  dense?: boolean;
  disabled?: boolean;
  duration?: number;
  errorMessage?: string;
  fitContainer?: boolean;
  group?: string;
  headerClass?: string;
  headerIconContainerStyle?: CSSProperties;
  headerIconStyle?: CSSProperties;
  headerStyle?: CSSProperties;
  hoverToggle?: boolean;
  label?: string;
  menu?: boolean;
  noPadding?: boolean;
  panelClass?: string;
  scrollContainer?: boolean;
  stickyHeader?: boolean;
}

interface PanelGroup {
  closePanel: () => void;
  content: HTMLDivElement;
}

interface PanelGroups {
  [key: string]: PanelGroup[];
}

const panelGroups: PanelGroups = {};

export default function useExpansionPanel(
  modelValue: Ref<Model>,
  props: Props
) {
  const expanded = ref(props.defaultOpened || modelValue.value);
  const container = ref<Nullable<HTMLDivElement>>(null);
  const content = ref<Nullable<HTMLDivElement>>(null);
  const containerHeight = ref(0);
  const inTransition = ref(false);
  const mouseover = ref(false);
  let transitionId = 0;

  // Consumers can override the collapse styles via CSS - so strictly test it.
  // a collapsed container is clipped to zero height but
  // still edge-touches the viewport, so an IntersectionObserver at threshold 0 reports it as visible.
  const { height: renderedHeight } = useElementSize(container);

  const isHidden = computed<boolean>(
    () => !expanded.value && renderedHeight.value === 0
  );

  const containerStyle = computed<CSSProperties>(() => {
    if (inTransition.value) {
      return {
        height: `${containerHeight.value}px`,
      };
    }

    return {};
  });

  async function updatePanel(): Promise<void> {
    if (!container.value || !content.value || !props.duration) return;

    transitionId += 1;
    const id = transitionId;
    inTransition.value = true;
    const from = container.value.offsetHeight;
    const to = expanded.value ? content.value.offsetHeight : 0;

    await executeTransition(containerHeight, from, to, {
      abort() {
        return id !== transitionId;
      },
      duration: props.duration,
      transition: TransitionPresets.easeInOutCubic,
    });

    if (id === transitionId) inTransition.value = false;
  }

  function closePanelGroup() {
    if (!props.group) {
      return;
    }

    panelGroups[props.group].forEach((panel: PanelGroup) => {
      if (panel.content === content.value) return;
      panel.closePanel();
    });
  }

  function togglePanel() {
    if (props.disabled) return;
    expanded.value = !expanded.value;
    closePanelGroup();
    updatePanel();
  }

  function openPanel() {
    if (expanded.value) return;
    expanded.value = true;
    updatePanel();
  }

  function closePanel() {
    if (!expanded.value || props.disabled) return;
    expanded.value = false;
    updatePanel();
  }

  function addToPanelGroup() {
    if (!props.group) return;

    if (!panelGroups[props.group]) {
      panelGroups[props.group] = [];
    }

    panelGroups[props.group].push({
      closePanel,
      content: content.value!,
    });
  }

  function removeFromPanelGroup() {
    if (!props.group || !panelGroups[props.group]?.length) return;

    panelGroups[props.group] = panelGroups[props.group].filter(
      (panel) => panel.content !== content.value
    );
  }

  function onMouseOver() {
    mouseover.value = true;
  }

  function onMouseOut() {
    mouseover.value = false;
  }

  watch(
    modelValue,
    () => {
      if (modelValue.value === expanded.value) return;
      expanded.value = modelValue.value;
      closePanelGroup();
      updatePanel();
    },
    { flush: "post" }
  );

  watch(expanded, () => {
    Object.assign(modelValue, {
      value: expanded.value,
    });
  });

  onMounted(() => {
    addToPanelGroup();
  });

  onBeforeUnmount(() => {
    removeFromPanelGroup();
  });

  return {
    closePanel,
    container,
    containerStyle,
    content,
    expanded,
    inTransition,
    isHidden,
    mouseover,
    onMouseOut,
    onMouseOver,
    openPanel,
    togglePanel,
  };
}
